import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API, formatApiError } from "../auth";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { X, ArrowLeft, ArrowRight, Camera, Clock, CheckCircle2, RotateCcw } from "lucide-react";

import { todayISO } from "../utils/date";
import { useFormDraft, usePhotoDraft } from "../hooks/useFormDraft";

// Returns HH:MM in browser local time.
const nowHHMM = () => {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

export default function NewBandeo() {
  const { authHeaders, token } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);

  const [loadingItem, setLoadingItem] = useState(isEditing);
  const draftKey = `draft:bandeo:new`;
  const draftEnabled = !isEditing;
  const [form, setForm, { clearDraft: clearFormDraft, restored: formRestored }] = useFormDraft(draftKey, {
    fecha: todayISO(),
    chain: "",
    branch: "",
    arrival_time: "",
    departure_time: "",
    delivery_qty: "",
    product_banded: "",
    banded_to_product: "",
    comments: "",
    arrival_lat: null,
    arrival_lng: null,
    arrival_accuracy: null,
  }, draftEnabled, { ignoreKeys: ["fecha"] });
  const [docPhotos, setDocPhotos, { clearDraft: clearDocDraft }] = usePhotoDraft(`draft:bandeo:new:docPhotos`, draftEnabled);
  const [tyingPhotos, setTyingPhotos, { clearDraft: clearTyingDraft }] = usePhotoDraft(`draft:bandeo:new:tyingPhotos`, draftEnabled);
  const [submitting, setSubmitting] = useState(false);

  // Notify user (once) if a draft was restored.
  useEffect(() => {
    if (formRestored) {
      // Force today's date; the draft may have been from a prior day.
      setForm((f) => (f.fecha === todayISO() ? f : { ...f, fecha: todayISO() }));
      toast.info("Borrador restaurado", {
        description: "Se recuperaron los datos que habías empezado a llenar.",
        duration: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRestored]);

  const discardDraft = () => {
    if (!window.confirm("¿Descartar el borrador y empezar de cero?")) return;
    clearFormDraft();
    clearDocDraft();
    clearTyingDraft();
    setForm({
      fecha: todayISO(),
      chain: "",
      branch: "",
      arrival_time: "",
      departure_time: "",
      delivery_qty: "",
      product_banded: "",
      banded_to_product: "",
      comments: "",
      arrival_lat: null,
      arrival_lng: null,
      arrival_accuracy: null,
    });
    setDocPhotos([]);
    setTyingPhotos([]);
    toast.success("Borrador descartado");
  };

  // Capture arrival time + geolocation. Time is set immediately. GPS is
  // captured SILENTLY in the background — no UI feedback whatsoever so the
  // user cannot tell whether coordinates were saved. The browser will still
  // show its native permission prompt the FIRST time (this is a browser
  // security requirement we cannot bypass); after that it's fully silent.
  const captureArrival = () => {
    update("arrival_time", nowHHMM());
    if (!("geolocation" in navigator)) return;

    const onSuccess = (pos) => {
      setForm((f) => ({
        ...f,
        arrival_lat: pos.coords.latitude,
        arrival_lng: pos.coords.longitude,
        arrival_accuracy: pos.coords.accuracy,
      }));
    };
    const noop = () => { /* silently ignore all errors */ };

    // High-accuracy attempt first; if it fails for any reason other than
    // explicit permission denial, silently retry with low-accuracy so we
    // still capture a coarse location.
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (err1) => {
        if (err1 && err1.code === err1.PERMISSION_DENIED) return;
        navigator.geolocation.getCurrentPosition(onSuccess, noop, {
          enableHighAccuracy: false, timeout: 20000, maximumAge: 300000,
        });
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    if (!isEditing) return;
    axios.get(`${API}/bandeos/${editId}`, { headers: authHeaders() })
      .then(({ data }) => {
        setForm({
          fecha: data.fecha,
          chain: data.chain || "",
          branch: data.branch || "",
          arrival_time: data.arrival_time || "",
          departure_time: data.departure_time || "",
          delivery_qty: data.delivery_qty || "",
          product_banded: data.product_banded || "",
          banded_to_product: data.banded_to_product || "",
          comments: data.comments || "",
          arrival_lat: data.arrival_lat ?? null,
          arrival_lng: data.arrival_lng ?? null,
          arrival_accuracy: data.arrival_accuracy ?? null,
        });
        setDocPhotos((data.delivery_doc_photos || []).map((p) => ({
          localId: crypto.randomUUID(), path: p, preview: `${API}/files/${p}`, uploading: false,
        })));
        setTyingPhotos((data.tying_photos || []).map((p) => ({
          localId: crypto.randomUUID(), path: p, preview: `${API}/files/${p}`, uploading: false,
        })));
      })
      .catch(() => {
        toast.error("No se pudo cargar el bandeo");
        navigate("/bandeos");
      })
      .finally(() => setLoadingItem(false));
  }, [editId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (files, setList, max) => {
    for (const file of files) {
      if (setList === setDocPhotos && docPhotos.length >= max) {
        toast.warning(`Máximo ${max} fotos del documento`);
        return;
      }
      const localId = crypto.randomUUID();
      const preview = URL.createObjectURL(file);
      setList((p) => [...p, { localId, path: null, preview, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        setList((p) => p.map((x) => x.localId === localId ? { ...x, path: data.path, uploading: false } : x));
      } catch (e) {
        toast.error("Error al subir foto");
        setList((p) => p.filter((x) => x.localId !== localId));
      }
    }
  };

  const onPickFiles = (setList, max) => (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files, setList, max);
    e.target.value = "";
  };
  const removePhoto = (setList) => (id) => setList((p) => p.filter((x) => x.localId !== id));

  const submit = async (e) => {
    e.preventDefault();
    if ([...docPhotos, ...tyingPhotos].some((p) => p.uploading)) {
      toast.warning("Espera a que terminen de subir las fotos");
      return;
    }
    if (!form.arrival_time) {
      toast.error("Marca primero la hora de llegada");
      return;
    }
    if (docPhotos.filter((p) => p.path).length === 0) {
      toast.error("Debes subir al menos 1 foto del documento de entrega");
      return;
    }
    // Capture departure time at the very moment of finishing the visit.
    // In edit mode, respect the previously captured departure_time.
    const departureTime = isEditing ? form.departure_time : (form.departure_time || nowHHMM());
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        departure_time: departureTime,
        delivery_doc_photos: docPhotos.map((p) => p.path).filter(Boolean),
        tying_photos: tyingPhotos.map((p) => p.path).filter(Boolean),
      };
      if (isEditing) {
        await axios.put(`${API}/bandeos/${editId}`, payload, { headers: authHeaders() });
        toast.success("Bandeo actualizado");
        navigate(`/bandeos/${editId}`);
      } else {
        await axios.post(`${API}/bandeos`, payload, { headers: authHeaders() });
        clearFormDraft();
        clearDocDraft();
        clearTyingDraft();
        toast.success("Bandeo guardado");
        navigate("/bandeos");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="new-bandeo-page">
      <button onClick={() => navigate(-1)} className="kbd-label flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="mb-8">
        <div className="kbd-label mb-2 text-emerald-700">/ {isEditing ? "Editar bandeo" : "Nuevo bandeo"}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
          {isEditing ? "Editar bandeo" : "Registro de bandeo"}
        </h1>
      </div>

      {loadingItem ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center kbd-label text-slate-400">Cargando…</div>
      ) : (
      <form onSubmit={submit} className="space-y-6" data-testid="bandeo-form">
        {formRestored && !isEditing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="draft-restored-banner">
            <RotateCcw className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900 text-sm">Borrador recuperado</div>
              <div className="text-xs text-slate-600 mt-0.5">Continuaste un bandeo que dejaste a medias. Tus datos y fotos ya subidas se restauraron.</div>
            </div>
            <button type="button" onClick={discardDraft} className="kbd-label text-amber-800 hover:text-amber-950 underline underline-offset-4" data-testid="discard-draft-btn">
              Descartar
            </button>
          </div>
        )}
        <Section title="Información general" step="01">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Fecha" required>
              <Input type="date" value={form.fecha} readOnly disabled className="bg-slate-100 text-slate-700 cursor-not-allowed" data-testid="field-fecha" />
              <p className="text-[11px] text-slate-500 mt-1.5">Fecha de hoy (no editable)</p>
            </Field>
            <Field label="Cadena / Cliente" required>
              <Input value={form.chain} onChange={(e) => update("chain", e.target.value)} required data-testid="field-chain" />
            </Field>
            <Field label="Punto de venta" required span>
              <Input value={form.branch} onChange={(e) => update("branch", e.target.value)} required data-testid="field-branch" />
            </Field>
            <Field label="Hora de llegada" required span>
              {form.arrival_time ? (
                <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="arrival-captured">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
                  <div className="flex-1">
                    <div className="kbd-label text-emerald-700">Hora registrada</div>
                    <div className="font-display text-2xl font-bold tracking-tight text-slate-950 tabular-nums leading-tight" data-testid="arrival-time-value">{form.arrival_time}</div>
                  </div>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={captureArrival}
                      className="kbd-label text-slate-500 hover:text-emerald-700 underline underline-offset-4"
                      data-testid="arrival-retake-btn"
                    >
                      Recapturar
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={captureArrival}
                  className="w-full flex items-center justify-center gap-2 rounded-md border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 px-5 py-3 font-display font-semibold tracking-tight transition-colors"
                  data-testid="arrival-capture-btn"
                >
                  <Clock className="w-5 h-5" />
                  Marcar hora de llegada
                </button>
              )}
              <p className="text-[11px] text-slate-500 mt-1.5">
                {form.arrival_time ? "Se capturó la hora del sistema al llegar al punto de venta." : "Presiona el botón cuando llegues al punto de venta. La hora se registra automáticamente."}
              </p>
            </Field>
          </div>
        </Section>

        <Section title="Entrega de producto" step="02" hint="Foto del documento de entrega (1-3 fotos). Solo cámara — no se permite subir desde galería.">
          <div className="mb-5">
            <label htmlFor="doc-camera-input" className="flex flex-col items-center justify-center border-2 border-dashed border-emerald-300 bg-emerald-50/50 hover:border-emerald-600 hover:bg-emerald-50 transition-colors rounded-lg p-6 cursor-pointer text-center" data-testid="doc-camera-trigger">
              <Camera className="w-7 h-7 text-emerald-700 mb-2" />
              <div className="font-display font-semibold text-slate-900 text-sm">Tomar foto del documento</div>
              <div className="kbd-label mt-1 !text-[9px]">Cámara · max 3</div>
              <input id="doc-camera-input" type="file" accept="image/*" capture="environment" multiple onChange={onPickFiles(setDocPhotos, 3)} className="hidden" data-testid="doc-camera-input" />
            </label>
          </div>
          {docPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              {docPhotos.map((p) => (
                <div key={p.localId} className="relative aspect-square bg-slate-100 rounded-md overflow-hidden group">
                  <img src={p.preview} alt="doc" className="w-full h-full object-cover" />
                  {p.uploading && <div className="absolute inset-0 bg-slate-950/50 flex items-center justify-center"><div className="kbd-label !text-white">Subiendo…</div></div>}
                  <button type="button" onClick={() => removePhoto(setDocPhotos)(p.localId)} className="absolute top-2 right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-doc-${p.localId}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Cajas / unidades entregadas" required span>
              <Input value={form.delivery_qty} onChange={(e) => update("delivery_qty", e.target.value)} required placeholder="Ej: 5 cajas de 12 unidades" data-testid="field-delivery-qty" />
            </Field>
          </div>
        </Section>

        <Section title="Atado de productos" step="03">
          <div className="grid md:grid-cols-2 gap-5 mb-5">
            <Field label="Producto atado" required>
              <Input value={form.product_banded} onChange={(e) => update("product_banded", e.target.value)} required placeholder="Ej: Crema dental 50ml" data-testid="field-product-banded" />
            </Field>
            <Field label="Producto al que va atado" required>
              <Input value={form.banded_to_product} onChange={(e) => update("banded_to_product", e.target.value)} required placeholder="Ej: Cepillo de dientes" data-testid="field-banded-to" />
            </Field>
          </div>

          <Label className="kbd-label">Fotos de registro de atados</Label>
          <p className="text-[11px] text-slate-500 mt-1 mb-2">Solo cámara — no se permite subir desde galería.</p>
          <div className="mb-3">
            <label htmlFor="tying-camera-input" className="flex flex-col items-center justify-center border-2 border-dashed border-emerald-300 bg-emerald-50/50 hover:border-emerald-600 hover:bg-emerald-50 transition-colors rounded-lg p-6 cursor-pointer text-center" data-testid="tying-camera-trigger">
              <Camera className="w-7 h-7 text-emerald-700 mb-2" />
              <div className="font-display font-semibold text-slate-900 text-sm">Tomar foto</div>
              <div className="kbd-label mt-1 !text-[9px]">Cámara</div>
              <input id="tying-camera-input" type="file" accept="image/*" capture="environment" multiple onChange={onPickFiles(setTyingPhotos, 20)} className="hidden" data-testid="tying-camera-input" />
            </label>
          </div>
          {tyingPhotos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-5">
              {tyingPhotos.map((p) => (
                <div key={p.localId} className="relative aspect-square bg-slate-100 rounded-md overflow-hidden group">
                  <img src={p.preview} alt="atado" className="w-full h-full object-cover" />
                  {p.uploading && <div className="absolute inset-0 bg-slate-950/50 flex items-center justify-center"><div className="kbd-label !text-white">Subiendo…</div></div>}
                  <button type="button" onClick={() => removePhoto(setTyingPhotos)(p.localId)} className="absolute top-2 right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-tying-${p.localId}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <Label className="kbd-label">Comentarios</Label>
            <Textarea rows={2} value={form.comments} onChange={(e) => update("comments", e.target.value)} className="mt-2" data-testid="field-comments" />
          </div>
        </Section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-3 pt-4 sticky bottom-0 bg-slate-50 p-4 -mx-5 md:-mx-8 lg:-mx-12 border-t border-slate-200">
          {!isEditing && (
            <p className="text-xs text-slate-500 sm:mr-auto max-w-md" data-testid="departure-hint">
              Al guardar se registrará automáticamente la <strong className="text-slate-700">hora de salida</strong> con la hora actual del sistema.
            </p>
          )}
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-slate-300" data-testid="cancel-btn">Cancelar</Button>
          <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white px-6 group" data-testid="submit-bandeo-btn">
            {submitting ? (
              isEditing ? "Actualizando…" : "Guardando…"
            ) : isEditing ? (
              <>Actualizar bandeo <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
            ) : (
              <><Clock className="mr-2 w-4 h-4" /> Marcar hora de salida y guardar</>
            )}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}

function Section({ title, step, hint, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-7">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-slate-100">
        <span className="kbd-label font-mono-tv bg-emerald-700 text-white px-2 py-1">{step}</span>
        <div>
          <h3 className="font-display text-xl font-bold text-slate-950">{title}</h3>
          {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, span, children }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <Label className="kbd-label">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
